import {
    combined,
    hasMinMaxChars,
    lowerCaseRequired,
    numberRequired,
    phoneNumber,
    phoneVerificationCode,
    required,
    upperCaseRequired,
    Validator,
} from '@perpay-web/validators/fieldBased';

/**
 * Validate form values with a given schema
 *
 * @param values - object mapping field name to value e.g.
 *  {
 *      first_name: "Rocky",
 *      last_name: "Balboa",
 *      phone: "555-555-5555",
 *      dateOfBirth: "07/06/1945",
 *  }
 * @param schema - object mapping field name to array of validator functions e.g.
 *  {
 *      first_name: [firstName],
 *      last_name: [lastName],
 *      phone: [phone],
 *      dateOfBirth: [required, dateFormat, minDateOfBirth],
 *  }
 */
export const validateForm = <T>(
    values: T,
    schema: Partial<{ [key in keyof T]: Validator[] | undefined }>,
) => {
    const fieldNames = Object.getOwnPropertyNames(values) as (keyof T)[];

    const errors = fieldNames.reduce((result, name) => {
        const fieldValidators = schema[name];

        const fieldValue = values[name];
        if (!fieldValidators) {
            return result;
        }

        const validators = combined(fieldValidators);
        const validationErrors = validators(fieldValue);
        if (!validationErrors) {
            return result;
        }

        return { ...result, [name]: validationErrors };
    }, {});

    return errors;
};

export const passwordSchema = {
    password: [
        required,
        lowerCaseRequired,
        upperCaseRequired,
        numberRequired,
        hasMinMaxChars,
    ],
};

export const phoneVerification = (values: { code: string }) => {
    const errors: { code?: string[] } = {};
    const codeValidators = combined([required, phoneVerificationCode]);

    const codeErrors = codeValidators(values.code);
    if (codeErrors) {
        errors.code = codeErrors;
    }

    return errors;
};

export const verifyPhoneNumberFormSchema = {
    code: [required, phoneVerificationCode],
};

export const setPhoneNumberFormSchema = {
    mobilePhone: [required, phoneNumber],
};
